export type DiscontinuityKind = 'removable' | 'jump' | 'infinite' | 'oscillating' | 'continuous';

/**
 * Estimate the one-sided limit of `f` as `x → a` from the given side.
 *
 * Samples `f(a ± 10^-k)` for k = 1..7, approaching from the left or right.
 * - Returns `±Infinity` if magnitudes diverge monotonically.
 * - Returns `NaN` if successive samples oscillate beyond tolerance (no convergence).
 * - Otherwise returns the last finite sample (closest to `a`).
 */
export function oneSidedLimit(f: (x: number) => number, a: number, side: 'left' | 'right'): number {
  const sign = side === 'right' ? 1 : -1;
  const samples: number[] = [];

  for (let k = 1; k <= 7; k++) {
    const x = a + sign * Math.pow(10, -k);
    samples.push(f(x));
  }

  const finiteSamples = samples.filter(Number.isFinite);

  // All samples non-finite → return the first one (will be ±Infinity or NaN from IEEE).
  if (finiteSamples.length === 0) {
    return samples[0]!;
  }

  // Detect oscillation: samples bounce sign or stay bounded without converging.
  // Key distinction: infinite limit → magnitudes grow monotonically in ONE sign direction.
  // Oscillating → values keep changing sign or don't converge within a bounded range.
  const lastFew = finiteSamples.slice(-4);
  if (lastFew.length >= 3) {
    const positives = lastFew.filter((v) => v > 0).length;
    const negatives = lastFew.filter((v) => v < 0).length;
    const mixedSign = positives > 0 && negatives > 0;

    if (mixedSign) {
      // Mixed-sign and bounded → oscillating.
      const absMax = Math.max(...lastFew.map(Math.abs));
      if (absMax < 1e6) {
        return NaN;
      }
    }
  }

  // Check if magnitudes are monotonically growing (diverging to ±Infinity).
  const magnitudesStrictlyGrowing = finiteSamples.every((v, i) => {
    if (i === 0) return true;
    return Math.abs(v) > Math.abs(finiteSamples[i - 1]!) * 1.5;
  });

  if (magnitudesStrictlyGrowing && Math.abs(finiteSamples[finiteSamples.length - 1]!) > 1e4) {
    const lastVal = finiteSamples[finiteSamples.length - 1]!;
    return lastVal > 0 ? Infinity : -Infinity;
  }

  // Return the last finite sample (closest converged estimate to a).
  return finiteSamples[finiteSamples.length - 1]!;
}

/**
 * Classify the discontinuity of `f` at `a`.
 *
 * Uses one-sided limits to determine kind:
 * - `'oscillating'` — either one-sided limit is NaN (no limit exists).
 * - `'infinite'`    — either one-sided limit is ±Infinity.
 * - `'jump'`        — both limits are finite but differ by more than ε.
 * - `'removable'`   — limits agree but differ from f(a) (or f(a) is non-finite).
 * - `'continuous'`  — limits agree and match f(a).
 */
export function classifyDiscontinuity(f: (x: number) => number, a: number): DiscontinuityKind {
  const L = oneSidedLimit(f, a, 'left');
  const R = oneSidedLimit(f, a, 'right');

  if (Number.isNaN(L) || Number.isNaN(R)) {
    return 'oscillating';
  }

  if (!Number.isFinite(L) || !Number.isFinite(R)) {
    return 'infinite';
  }

  const epsilon = 1e-6;

  if (Math.abs(L - R) > epsilon) {
    return 'jump';
  }

  // Limits agree — compare to f(a).
  let fa: number;
  try {
    fa = f(a);
  } catch {
    fa = NaN;
  }

  if (!Number.isFinite(fa) || Math.abs(fa - L) > epsilon) {
    return 'removable';
  }

  return 'continuous';
}
